<template>
  <div :class="$x.root">
    <div
      v-if="!adrOnly"
      :class="[$x.row, $x.wrap]"
      itemprop="offers"
      itemscope
      itemtype="http://schema.org/Offer"
    >
      <!-- -------------- -->
      <!-- ORIGINAL PRICE -->
      <!-- -------------- -->
      <template v-if="showOriginal">
        <div :class="[$x.row, $x.wrap, $x.marginBottom, $x.strikeThrough]">
          <!-- LABEL -->
          <span v-if="showLabels && originalPriceLabel" :class="$x.label">{{
            originalPriceLabel + "&nbsp;"
          }}</span>

          <!-- PRICE -->
          <NsCurrency
            :class="$x.price"
            :amount="originalPrice"
            :price-type="originalPriceType"
            itemprop="price"
            data-testid="original-price"
            strike-through
          />
        </div>
      </template>

      <!-- ----- -->
      <!-- PRICE -->
      <!-- ----- -->

      <div :class="[$x.row, $x.wrap, $x.marginBottom]">
        <!-- LABEL (SHOWN WHEN ADR) -->
        <span v-if="showLabels && priceLabel" :class="$x.label">{{
          priceLabel + "&nbsp;"
        }}</span>

        <!-- PRICE -->
        <NsCurrency
          :class="$x.price"
          :amount="isBaseSku ? minPrice : price"
          :price-type="priceType"
          itemprop="price"
          :data-testid="isBaseSku ? 'min-price' : 'price'"
        />
        <!-- SEPARATOR -->
        <span v-if="showMinMax" :class="$x.priceSeparator">-</span>
        <!-- MAX PRICE -->
        <NsCurrency
          v-if="showMinMax"
          :class="$x.price"
          :amount="maxPrice"
          :price-type="priceType"
          itemprop="price"
          data-testid="max-price"
        />
      </div>
    </div>

    <!-- --------- -->
    <!-- ADR PRICE -->
    <!-- --------- -->
    <div v-if="showAdr" :class="[$x.row, $x.marginBottom]">
      <!-- RIGHT LABEL (SHOWN WHEN ADR) -->
      <span v-if="adrPriceLabel" :class="$x.label">{{
        adrPriceLabel + "&nbsp;"
      }}</span>
      <!-- ADR PRICE -->
      <NsCurrency
        :class="$x.price"
        :amount="adrPrice"
        :price-type="adrPriceType"
        itemprop="price"
        data-testid="adr-price"
      />
    </div>
  </div>
</template>

<script>
import NsCurrency from "./NsCurrency.vue";

/**
 * How to use: 'original' price should be retail, except during a sale and the
 * user is logged in, then show wholesale. 'price' is the actual selling price
 * (retail, wholesale, sale, etc.). Always shows selling price. If original
 * price is different and 'showOriginalCrossedOut' is true it will show the
 * original price crossed out. By default, ADR price is only shown if its value
 * is different than the selling price.
 */
export default {
  name: "NsProductPrice",
  components: { NsCurrency },
  props: {
    /**
     * Will show only the Adr Price and label
     */
    adrOnly: Boolean,
    /**
     * The ADR Price
     */
    adrPrice: {
      type: String,
      default: ""
    },
    /**
     * The Label for ADR Price
     */
    adrPriceLabel: {
      type: String,
      default: ""
    },
    /**
     * The priceType of the ADR amount
     */
    adrPriceType: {
      type: String,
      default: ""
    },
    /**
     * If true, will override hiding all components associated with ADR
     */
    hideAdrOverride: Boolean,
    /**
     * If true, will show minPrice and maxPrice if different.
     */
    isBaseSku: Boolean,
    /**
     * Min Actual Price (if base sku)
     */
    minPrice: {
      type: String,
      default: ""
    },
    /**
     * Max Actual Price (if base sku)
     */
    maxPrice: {
      type: String,
      default: ""
    },
    /**
     * Retail Price
     */
    originalPrice: {
      type: String,
      default: ""
    },
    /**
     * Label for Retail Price
     */
    originalPriceLabel: {
      type: String,
      default: ""
    },
    /**
     * The priceType of the original amount
     */
    originalPriceType: {
      type: String,
      default: ""
    },
    /**
     * Actual price for user
     */
    price: {
      type: String,
      default: ""
    },
    /**
     * Label for actual price type (Wholesale, Retail Sale, etc.)
     */
    priceLabel: {
      type: String,
      default: ""
    },
    /**
     * The priceType of the amount
     */
    priceType: {
      type: String,
      default: ""
    },
    /**
     * If true, will show the ADR price, even if it's the same as price
     */
    showAdrPrice: Boolean,
    /**
     * If true, will show price labels
     */
    showLabels: Boolean,
    /**
     * If true, will show original price crossed out
     */
    showOriginalCrossedOut: Boolean
  },
  computed: {
    showOriginal() {
      return (
        !this.isBaseSku &&
        this.showOriginalCrossedOut &&
        this.originalPrice > this.price
      );
    },

    showMinMax() {
      return this.isBaseSku && this.minPrice !== this.maxPrice;
    },

    showAdr() {
      return (
        !this.hideAdrOverride &&
        this.adrPrice &&
        (this.showAdrPrice || this.adrPrice !== this.price || this.adrOnly)
      );
    }
  }
};
</script>
<style module></style>

<style lang="scss" module="$x">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/typography";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

.root {
  position: relative;
  @extend .p-medium;
}

.row {
  display: flex;
  justify-content: inherit;
}

.marginBottom {
  margin-bottom: 8px;
}

.wrap {
  flex-wrap: wrap;
}

.strikeThrough {
  position: relative;
  display: flex;
  align-items: center;
  margin-right: 12px;
  color: map-get(colors.$themeColors, color-interactive-8);
  text-decoration: line-through;

  .price {
    font-weight: map-get(typographyVariables.$typography, font-weight-regular);
  }
}

.price {
  position: relative;
  display: inline-block;
  font-weight: map-get(typographyVariables.$typography, font-weight-semibold);
}

.label {
  margin: 0;
}

.priceSeparator {
  padding: 0 10px;
}
</style>
